import React from "react";

const inline = (text, keyPrefix) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.filter(Boolean).map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={`${keyPrefix}-${i}`} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={`${keyPrefix}-${i}`}>{p}</React.Fragment>
    )
  );
};

/** Renderizador markdown minimalista (títulos, listas, citações, negrito). */
export const Markdown = ({ content = "" }) => {
  const lines = String(content).replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let list = null;
  let para = [];

  // Chave estável por bloco: tipo + posição de origem + início do texto.
  // Evita o anti-padrão de usar apenas o índice do array.
  const keyFor = (type, text, line) => `${type}-${line}-${String(text).slice(0, 24).replace(/\s+/g, "_")}`;

  const flushPara = () => {
    if (para.length) {
      const text = para.join(" ");
      blocks.push({ type: "p", text, key: keyFor("p", text, blocks.length) });
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push({ type: "ul", items: list, key: keyFor("ul", list[0] || "", blocks.length) });
      list = null;
    }
  };

  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) {
      flushPara();
      flushList();
      return;
    }
    if (line.startsWith("### ")) {
      flushPara(); flushList();
      blocks.push({ type: "h3", text: line.slice(4), key: keyFor("h3", line.slice(4), blocks.length) });
      return;
    }
    if (line.startsWith("## ")) {
      flushPara(); flushList();
      blocks.push({ type: "h2", text: line.slice(3), key: keyFor("h2", line.slice(3), blocks.length) });
      return;
    }
    if (line.startsWith("# ")) {
      flushPara(); flushList();
      blocks.push({ type: "h2", text: line.slice(2), key: keyFor("h2", line.slice(2), blocks.length) });
      return;
    }
    if (line.startsWith("> ")) {
      flushPara(); flushList();
      blocks.push({ type: "quote", text: line.slice(2), key: keyFor("quote", line.slice(2), blocks.length) });
      return;
    }
    if (/^[-*]\s+/.test(line)) {
      flushPara();
      list = list || [];
      list.push(line.replace(/^[-*]\s+/, ""));
      return;
    }
    flushList();
    para.push(line);
  });
  flushPara();
  flushList();

  return (
    <div className="space-y-5 text-slate-600 leading-relaxed">
      {blocks.map((b, i) => {
        if (b.type === "h2")
          return (
            <h2 key={b.key} className="text-xl sm:text-2xl font-bold text-slate-900 pt-4 first:pt-0 scroll-mt-24">
              {inline(b.text, `h2-${i}`)}
            </h2>
          );
        if (b.type === "h3")
          return (
            <h3 key={b.key} className="text-base sm:text-lg font-bold text-slate-800 pt-2">
              {inline(b.text, `h3-${i}`)}
            </h3>
          );
        if (b.type === "quote")
          return (
            <blockquote key={b.key} className="border-l-4 border-amber-400 bg-amber-50/70 rounded-r-lg px-4 py-3 text-sm text-slate-700">
              {inline(b.text, `q-${i}`)}
            </blockquote>
          );
        if (b.type === "ul")
          return (
            <ul key={b.key} className="space-y-2 pl-1">
              {b.items.map((it, j) => (
                <li key={`${b.key}-${j}-${it.slice(0, 16)}`} className="flex gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                  <span>{inline(it, `li-${i}-${j}`)}</span>
                </li>
              ))}
            </ul>
          );
        return <p key={b.key}>{inline(b.text, `p-${i}`)}</p>;
      })}
    </div>
  );
};
